import type {
  CreateOptions,
  CreateTargetStatusOptions,
  CreationValues,
} from '@cims-online/shared'
import { Button, Space } from 'antd'
import CIMSButton from 'components/CIMSButton'
import CIMSDrawer from 'components/CIMSDrawer'
import { simpleCreateNotification } from 'hooks/notificationHandler'
import { useCallback, useRef, useState } from 'react'
import { useCreateWorkflowRecordFromRecordsMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import CreateWorkflowRecordForm, {
  CREATE_WORKFLOW_RECORD_FORM_ID,
} from './components/CreateWorkflowRecordForm'

type CreateWorkflowRecordDrawerProps = {
  createOptions: CreateOptions
  onClose: () => void
  onCreated: () => Promise<unknown>
  open: boolean
  workflowId: string
}

/** Displays the fields configured for creating a workflow record. */
function CreateWorkflowRecordDrawer({
  createOptions,
  onClose,
  onCreated,
  open,
  workflowId,
}: CreateWorkflowRecordDrawerProps) {
  const targetStatuses = createOptions.targetStatuses.filter(
    ({ enabled, valid }) => enabled && valid
  )
  const [formValid, setFormValid] = useState(false)
  const [submitLabel, setSubmitLabel] = useState(
    `${targetStatuses[0]?.actionLabel ?? 'Create'} ${
      createOptions.genericLabel
    }`
  )
  const submitting = useRef(false)
  const [submittingState, setSubmittingState] = useState(false)
  const [createWorkflowRecord, { loading }] =
    useCreateWorkflowRecordFromRecordsMutation(DefaultMutationHookOptions)
  const mutationPending = loading || submittingState

  const handleClose = useCallback(() => {
    if (!submitting.current && !loading) onClose()
  }, [loading, onClose])

  const handleTargetChange = useCallback(
    ({ actionLabel }: CreateTargetStatusOptions) =>
      setSubmitLabel(`${actionLabel} ${createOptions.genericLabel}`),
    [createOptions.genericLabel]
  )

  const handleSubmit = useCallback(
    async (
      values: CreationValues,
      targetStatus: CreateTargetStatusOptions['targetStatus']
    ) => {
      if (submitting.current) return

      submitting.current = true
      setSubmittingState(true)
      try {
        const result = await createWorkflowRecord({
          variables: {
            input: { targetStatus, values, workflowId },
          },
        })
        const response = result.data?.createWorkflowRecord
        if (!response) return

        onClose()
        try {
          await onCreated()
        } finally {
          simpleCreateNotification(response.created.recordName)
        }
      } catch {
        // The existing mutation and query hooks display request errors.
      } finally {
        submitting.current = false
        setSubmittingState(false)
      }
    },
    [createWorkflowRecord, onClose, onCreated, workflowId]
  )

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Space>
        <Button disabled={mutationPending} onClick={handleClose}>
          Cancel
        </Button>
        <CIMSButton
          disabled={{
            disabled: !formValid,
            reason: {
              why: 'disabled',
              message: 'Input all required fields to create the record',
            },
          }}
          form={CREATE_WORKFLOW_RECORD_FORM_ID}
          htmlType="submit"
          loading={mutationPending}
          type="primary"
        >
          {submitLabel}
        </CIMSButton>
      </Space>
    </div>
  )

  return (
    <CIMSDrawer
      footer={footer}
      onClose={handleClose}
      open={open && targetStatuses.length > 0}
      title={createOptions.dialogLabel}
    >
      {targetStatuses.length > 0 && (
        <CreateWorkflowRecordForm
          onSubmit={handleSubmit}
          onTargetChange={handleTargetChange}
          onValidityChange={setFormValid}
          submitting={mutationPending}
          targetStatuses={targetStatuses}
        />
      )}
    </CIMSDrawer>
  )
}

export default CreateWorkflowRecordDrawer
